import Config from 'config';
import { useUnit } from 'effector-react';
import $$api from 'models/api';
import $$auth from 'models/auth';
import $$weakPassword from 'models/change-weak-password';
import { $isDesktop } from 'models/device-params-model';
import $$ep from 'models/ep';
import {
    $isAdvisorClientPage,
    $isAdvisorPanel,
    $isEmployersPage,
    $isHeaderlessPage,
    $isTacPage,
    $pathname,
    $queryParams,
} from 'models/location';
import { $global } from 'models/store-global';
import { $modals } from 'models/store-modals';
import { useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import Dropdown from 'components/StyledTemplates/Dropdown';
import Hamburger from 'components/StyledTemplates/Hamburger';
import { Button, Frame, Link } from 'components/StyledTemplates/StyledTemplates';
import Tooltip from 'components/StyledTemplates/Tooltip';
import AdvisorClientMenu from 'components/pages/AdvisorClientDashboard/AdvisorClientMenu';
import Menu from 'components/pages/Dashboard/Menu';
import QuestionnaireMenu from 'components/pages/Questionnaire/QuestionnaireMenu';

import env from 'constants/env';

import AuthService from 'services/AuthService';
import EstatePlanService from 'services/EstatePlanService';
import LoginAsUserService from 'services/LoginAsUserService';
import TempUsersService from 'services/TempUsersService';

import useResources from 'hooks/useResources';

import AdvisorsLogo from 'assets/OriginLogo.png';
import Logo from 'assets/OriginLogo.png';
import imgIconArrowDown from 'assets/icon-arrow-down.svg';

const { isMarketing } = Config;

const $isTrustFundingQuestionnaire = $pathname.map((pathname) => pathname.split('/').slice(1)?.[1] === 'TRUST_FUND');
const $isGiftTrustQuestionnaire = $pathname.map((pathname) => pathname.split('/').slice(1)?.[1] === 'GIFT_TRUST');
const $isEpQuestionnaire = $pathname.map((pathname) => pathname.split('/').slice(1)?.[1] === 'EP');

const HeyLabel = () => {
    const participant = useUnit($$auth.$currentParticipant);
    const { t } = useResources();
    return (
        <Frame data-qaid="header_profile" extra="flex-direction: row;">
            {participant?.participant_id ? (
                <div className="name-div">{t(`navHeader.myProfile`)}</div>
            ) : (
                <Spinner animation="border" variant="primary" />
            )}
        </Frame>
    );
};

const ProfileDropdownContent = () => {
    const { t } = useResources();
    const user = useUnit($global.map((state) => state.auth?.user));
    const isTempUser = TempUsersService.isTempUser(user?.attributes?.email || '');
    const currentParticipant = useUnit($$auth.$currentParticipant);
    const isAdvisor = (currentParticipant?.systemRoles ?? []).includes('advisor');
    const isGroupAdmin =
        Array.isArray(currentParticipant?.administratedGroups) &&
        (currentParticipant?.administratedGroups || []).length > 0;

    const isAdvisorPanel = useUnit($isAdvisorPanel);

    return (
        <>
            {!isTempUser && (
                <>
                    <HeaderStyles.ProfileMenuItem
                        data-qaid="header_profile_dashboard"
                        to={isAdvisorPanel ? '/advisor/dashboard' : '/'}
                    >
                        {t('navHeader.dashboard')}
                    </HeaderStyles.ProfileMenuItem>
                    <HeaderStyles.ProfileMenuItem
                        data-qaid="header_profile_account"
                        to={isAdvisorPanel ? '/advisor/profile' : '/profile'}
                    >
                        {t('navHeader.account')}
                    </HeaderStyles.ProfileMenuItem>
                    {!isAdvisorPanel && (
                        <HeaderStyles.ProfileMenuItem
                            data-qaid="header_profile_billing"
                            as={Frame}
                            onClick={$$api.misc.getBillingAndPaymentsLinkFx}
                        >
                            {t('navHeader.billing')}
                        </HeaderStyles.ProfileMenuItem>
                    )}
                </>
            )}
            {isAdvisor && !isAdvisorPanel && (
                <HeaderStyles.ProfileMenuItem data-qaid="header_profile_advisor_dashboard" to="/advisor/dashboard">
                    {t('navHeader.backToClientList')}
                </HeaderStyles.ProfileMenuItem>
            )}
            {isGroupAdmin && isAdvisorPanel && (
                <HeaderStyles.ProfileMenuItem data-qaid="header_profile_advisor_groups" to="/advisor/groups">
                    {t('navHeader.groups')}
                </HeaderStyles.ProfileMenuItem>
            )}
            <HeaderStyles.ProfileMenuItem data-qaid="header_profile_support" support>
                {t('navHeader.support')}
            </HeaderStyles.ProfileMenuItem>
            {LoginAsUserService.isLoggedInAsUser() && (
                <HeaderStyles.ProfileMenuItem
                    data-qaid="header_profile_resume"
                    as={Frame}
                    onClick={async () => {
                        await LoginAsUserService.resume();
                    }}
                    extra={({ theme }: any) => `font-weight: 600; color: ${theme.primary};`}
                    className="d-none d-lg-flex"
                >
                    {t('navHeader.resume', { name: LoginAsUserService.getOriginalUserName() })}
                </HeaderStyles.ProfileMenuItem>
            )}
            <HeaderStyles.ProfileMenuItem
                data-qaid="header_profile_sign_out"
                as={Frame}
                onClick={AuthService.logout}
                extra={({ theme }: any) => `font-weight: 600; color: ${theme.primary};`}
                className="d-none d-lg-flex"
            >
                {t('navHeader.signOut')}
            </HeaderStyles.ProfileMenuItem>
        </>
    );
};

const NavMenuContent = () => {
    const { t } = useResources();

    const isDesktop = useUnit($isDesktop);
    // const { role } = useUnit($queryParams);

    const user = useUnit($global.map((state) => state.auth?.user));
    const ep = useUnit($$ep.stores.$epQInstance);
    const me = useUnit($$auth.$currentParticipant);

    // const isAdvisorSignPages = role === 'advisor';
    const isAdvisorSignPages = false;
    const isAdvisorPanel = useUnit($isAdvisorPanel) || isAdvisorSignPages;
    const isEmployersPage = useUnit($isEmployersPage);
    const isLoggedIn =
        !!user?.attributes && ((isAdvisorPanel && (me?.systemRoles || []).includes('advisor')) || !isAdvisorPanel);

    const isTempUser = TempUsersService.isTempUser(user?.attributes?.email || '');

    if (isEmployersPage) {
        return (
            <a href="#employer-sales">
                <Button data-qaid="get_in_touch_button" className="px-4 my-3">
                    {t('navHeader.getInTouch')}
                </Button>
            </a>
        );
    }

    return (
        <Frame
            className="d-flex flex-lg-row"
            extra="width: 100%; height: 100%; justify-content: space-between; @media (max-width: 991px) { align-items: flex-start; };"
        >
            <Frame className="d-flex flex-lg-row w-100">
                {isLoggedIn && !isDesktop && <ProfileDropdownContent />}
                {isAdvisorPanel && (
                    <>
                        {isLoggedIn && !isMarketing && (
                            <>
                                <HeaderStyles.NavItem to="/advisor/clients">
                                    {t('navHeader.advisor.clients')}
                                </HeaderStyles.NavItem>
                                <HeaderStyles.NavItem marketing to="/advisors/resources">
                                    {t('navHeader.advisor.marketingMaterials')}
                                </HeaderStyles.NavItem>
                            </>
                        )}
                    </>
                )}
                {isLoggedIn && !isTempUser && <HeaderStyles.VerticalDiv />}
                {isLoggedIn && !isTempUser && !(isMarketing && isAdvisorPanel) && (
                    <Dropdown
                        id="profileContextMenu"
                        alloweToClickClassesList={['hamburger']}
                        toggle={<HeyLabel />}
                        menu={<ProfileDropdownContent />}
                        toggleProps={{ className: 'd-none d-lg-flex', extra: 'padding: 0 0.5rem;' }}
                        menuProps={{
                            className: 'd-none d-lg-flex',
                            extra: 'padding: 15px 0px; width: max-content;',
                        }}
                    />
                )}
            </Frame>
            <Frame
                className="flex-column-reverse flex-lg-row"
                extra="white-space: pre; @media (max-width: 991px) { padding: 15px; width: 100%; border-top: 1px solid #E6E6E6; };"
            >
                {!isLoggedIn ? (
                    <>
                        {isAdvisorPanel ? (
                            <AdvisorButtonsWrapper>
                                {!user?.attributes && (
                                    <HeaderStyles.NavItem marketing={false} to="/?role=advisor">
                                        Sign in
                                    </HeaderStyles.NavItem>
                                )}
                            </AdvisorButtonsWrapper>
                        ) : (
                            <>
                                <Link marketing={false} to={isMarketing ? '/logout' : '/'}>
                                    <HeaderStyles.SignInButton
                                        data-qaid="sign_in_button"
                                        variant="light"
                                        className="mr-1"
                                        onClick={AuthService.logout}
                                    >
                                        {t('signUp.signIn')}
                                    </HeaderStyles.SignInButton>
                                </Link>
                                {ep?.questionnaire_instance_id && (
                                    <Link
                                        marketing={false}
                                        to={`/document/${ep.questionnaire_type_code}/${ep.state_code}/${ep?.questionnaire_instance_id}`}
                                    >
                                        <Button data-qaid="back_or_get_started_button" className="px-4 my-3">
                                            {t(isTempUser ? 'navHeader.backToAssessment' : 'navHeader.getStarted')}
                                        </Button>
                                    </Link>
                                )}
                            </>
                        )}
                    </>
                ) : (
                    <>
                        {isAdvisorPanel && isMarketing ? (
                            <>
                                <Link marketing={false} to="/advisor/dashboard">
                                    <Button data-qaid="back_button" variant="light">
                                        {t('navHeader.backToAdvisorDashboard')}
                                    </Button>
                                </Link>
                            </>
                        ) : (
                            <>
                                <Button
                                    data-qaid="sign_out_button"
                                    className="btn-permission w-100 d-block d-lg-none"
                                    onClick={AuthService.logout}
                                >
                                    {t(`navHeader.signOut`)}
                                </Button>

                                {LoginAsUserService.isLoggedInAsUser() && (
                                    <Button
                                        data-qaid="resume_button"
                                        onClick={async () => {
                                            await LoginAsUserService.resume();
                                        }}
                                        className="btn-permission w-100 d-block d-lg-none mb-2"
                                    >
                                        {t('navHeader.resume', { name: LoginAsUserService.getOriginalUserName() })}
                                    </Button>
                                )}
                            </>
                        )}
                    </>
                )}
            </Frame>
        </Frame>
    );
};

const Header = () => {
    const { role } = useUnit($queryParams);
    const navigate = useNavigate();
    const isDesktop = useUnit($isDesktop);

    const { t } = useResources();

    const estatePlanLoaded = useUnit($global.map((state) => state.temp?.estatePlanLoaded === true));
    const isAuthenticated = useUnit($global.map((state) => state.auth?.isAuthenticated === true));
    const email = useUnit($global.map((state) => state.auth?.user?.attributes?.email || ''));
    const isTacPage = useUnit($isTacPage);
    const isHeaderlessPage = useUnit($isHeaderlessPage);
    const isTrustFundingQuestionnaire = useUnit($isTrustFundingQuestionnaire);
    const isGiftTrustQuestionnaire = useUnit($isGiftTrustQuestionnaire);
    const isEpQuestionnaire = useUnit($isEpQuestionnaire);
    const isTempUser = TempUsersService.isTempUser(email);
    const isAdvisorSignPages = role === 'advisor';
    const isAdvisorPanel = useUnit($isAdvisorPanel) || isAdvisorSignPages;
    const isAdvisorClientPage = useUnit($isAdvisorClientPage);
    const systemRoles = useUnit($$auth.$systemRoles);
    const isAdvisor = systemRoles.includes('advisor');
    const isLoggedInAsUser = LoginAsUserService.isLoggedInAsUser();
    const isModalAssessmentOpened = useUnit($modals.map((modals) => modals.MODAL_ASSESSMENT?.isOpened === true));
    const isRelevantSigned = useUnit($global.map((state) => state?.tac?.user?.isRelevantSigned || false));
    const isPasswordChangeRequired = useUnit($$weakPassword.$changeRequired);

    const handlers = {
        onLogoClick: isTempUser ? AuthService.logout : () => {},
        signOut: AuthService.logout,
        exitDocumentPage: async () => {
            if (isEpQuestionnaire) {
                await EstatePlanService.saveAnswers();
            }
            if (isTempUser) {
                window.location.href = env.REACT_APP_MSITE_URL;
            } else {
                if (isTrustFundingQuestionnaire) {
                    navigate('/dashboard/trust-funding');
                } else {
                    if (isGiftTrustQuestionnaire) {
                        navigate('/dashboard/gift-trust');
                    } else {
                        navigate('/');
                    }
                }
            }
        },
    };

    useEffect(() => {
        if (!estatePlanLoaded && isAuthenticated && isRelevantSigned) {
            EstatePlanService.loadEstatePlan();
        }
    }, [estatePlanLoaded, isAuthenticated, isRelevantSigned]);

    return (
        <>
            <HeaderStyles.EmptyBlock />
            <HeaderStyles.Wrapper
                isDocumentPage={isHeaderlessPage && isDesktop}
                isModalAssessmentOpened={isModalAssessmentOpened}
            >
                <HeaderStyles.Logo
                    data-qaid="header_logo"
                    marketing={true}
                    onClick={handlers.onLogoClick}
                    to={isAdvisorPanel ? '/advisors' : '/'}
                >
                    <img alt="logo" src={isAdvisorPanel ? AdvisorsLogo : Logo} />
                </HeaderStyles.Logo>
                {isAdvisor && !isAdvisorPanel && !isLoggedInAsUser && isDesktop && !isPasswordChangeRequired && (
                    <HeaderLinkWrapper>
                        <Link
                            className="ml-2 mr-2"
                            to={isMarketing ? '/advisor/dashboard?resumeAsAdvisor=true' : '/advisor/dashboard'}
                        >
                            {t('navHeader.backToAdvisorDashboard')}
                        </Link>
                    </HeaderLinkWrapper>
                )}
                {isLoggedInAsUser && !isPasswordChangeRequired && (
                    <HeaderLinkWrapper>
                        <Link
                            className="ml-2 mr-2"
                            to=""
                            onClick={async (e: any) => {
                                e.preventDefault();
                                await LoginAsUserService.resume();
                            }}
                        >
                            {t('navHeader.resume', { name: LoginAsUserService.getOriginalUserName() })}
                        </Link>
                    </HeaderLinkWrapper>
                )}
                {!isPasswordChangeRequired && (
                    <div className="d-flex flex-row align-items-center justify-cone">
                        {!isTacPage && isHeaderlessPage && (isLoggedInAsUser || (isAdvisor && !isLoggedInAsUser)) && (
                            <HeaderStyles.VerticalDiv />
                        )}
                        {!isTacPage &&
                            (isHeaderlessPage && isDesktop ? (
                                <Tooltip
                                    label={
                                        isTrustFundingQuestionnaire
                                            ? t('header.backToTrustFunding')
                                            : t('header.backToDashboard')
                                    }
                                    side="bottom"
                                >
                                    <strong onClick={handlers.exitDocumentPage}>
                                        {isTrustFundingQuestionnaire ? t('header.trustFunding') : t('header.dashboard')}
                                    </strong>
                                </Tooltip>
                            ) : isHeaderlessPage ? (
                                <Dropdown
                                    toggle={
                                        <Hamburger.Icon id="profileContextMenu" className="mr-3 d-flex d-lg-none" />
                                    }
                                    menu={<QuestionnaireMenu />}
                                />
                            ) : (
                                <>
                                    <HeaderStyles.RightWrapper>
                                        <NavMenuContent />
                                    </HeaderStyles.RightWrapper>
                                    <Hamburger.Icon id="profileContextMenu" className="mr-3 d-flex d-lg-none" />
                                    <Hamburger.SidebarMenu
                                        id="profileContextMenu"
                                        className="d-flex d-lg-none"
                                        navMenuContent={NavMenuContent}
                                        menu={isAdvisorClientPage ? AdvisorClientMenu : Menu}
                                    />
                                </>
                            ))}
                    </div>
                )}
                {isPasswordChangeRequired && (
                    <button data-qaid="sign_out_button" className="btn-primary mr-2" onClick={AuthService.logout}>
                        {t('navHeader.back')}
                    </button>
                )}
            </HeaderStyles.Wrapper>
        </>
    );
};

const HeaderLinkWrapper = styled(Frame)`
    width: 100%;
    flex-direction: row;
    justify-content: flex-end;
    font-weight: 600;
`;

const AdvisorButtonsWrapper = styled.div`
    display: flex;

    > * {
        margin-right: 1rem;
    }

    @media (max-width: 768px) {
        flex-direction: column;
        > * {
            justify-content: center;
            margin: 0 0 15px 0;
        }
    }
`;

const HeaderStyles = {
    EmptyBlock: styled(Frame)`
        height: 70px;
    `,
    ProfileMenuItem: styled(Link)`
        width: 100%;
        cursor: pointer;
        color: #4b4b4b;
        padding: 14px 30px;
        font-weight: 400;
        font-size: 20px;
        line-height: 24px;
        text-align: center;

        &:hover {
            background-color: #f8f9fa;
            text-decoration: none;
        }

        @media (max-width: 991px) {
            /*font-family: Marcellus, serif;*/
            font-size: 15px;
            font-weight: normal;
            line-height: 19px;
            color: #343a40;
            width: 100%;
            align-items: flex-start;
            padding: 10px 25px;
            text-align: left;

            ${({ as: As }: any) =>
                As?.target === `div` &&
                css`
                    padding: 10px 20px 10px 25px;
                    width: 100%;
                    justify-content: space-between;
                `}
        }

        ${({ extra }: any) => extra}
    `,
    OurPlansWrapper: styled(Frame)<{ $opened: boolean }>`
        @media (max-width: 991px) {
            width: 100%;
            align-items: flex-start;
            padding: 25px;
            background: #faf9f8;
            border: 1px solid #e6e6e6;
            border-right: 0px;
            border-left: 0px;
            overflow: hidden;
            transform: translateX(${({ $opened = false }) => ($opened ? '0' : '100%')});

            ${({ $opened = false }) =>
                !$opened &&
                css`
                    height: 0;
                    opacity: 0;
                    padding: 0px;
                    border: 0px;
                `}
        }
    `,
    OurPlans: styled(Link)`
        margin: 16px 0 5px 0;
        transition: 0.2s;

        &:hover {
            opacity: 0.75;
            transform: translateX(2px);
            text-decoration: none;
        }

        h1 {
            /*font-family: Marcellus, sans-serif;*/
            font-size: 22px;
            font-weight: 400;
            color: ${({ theme }) => theme.primary};
        }
        p {
            font-size: 14px;
            margin: -5px 0;
            color: black;
        }

        @media (max-width: 991px) {
            > * {
                font-size: 13px;
            }
            p {
                line-height: 15px;
                color: #6c757d;
            }
        }
    `,
    SignInButton: styled(Button).attrs((props) => {
        return {
            ...props,
        };
    })`
        color: ${({ theme }) => theme.primary};
        padding: 8px 16px !important;
    `,
    Wrapper: styled(Frame).attrs((props) => {
        return { ...props, className: 'shadow-sm d-flex flex-row justify-content-between' };
    })<{ isDocumentPage: boolean; isModalAssessmentOpened: boolean }>`
        height: 70px;
        width: 100vw;
        background: white;
        z-index: ${({ isModalAssessmentOpened }) => (isModalAssessmentOpened ? 4 : 3)};
        position: ${isMarketing ? 'absolute' : 'fixed'};
        top: 0;
        left: 0;
        padding-right: ${({ isDocumentPage }) => (isDocumentPage ? 4 : 0)}rem;
    `,
    Logo: styled(Link)`
        margin-left: 1.5rem;
        cursor: pointer;
        img {
            height: 32px;
        }

        @media (max-width: 991px) {
            img {
                height: 1.5rem;
            }
        }
    `,
    RightWrapper: styled(Frame).attrs((props) => {
        return { ...props, className: `d-none d-lg-flex flex-row justify-content-end mr-5` };
    })`
        margin-right: 3rem;

        .name-div {
            font-weight: 600;
            font-size: 16px;
            color: #565656;
            /* margin-right: 10px; */
            white-space: nowrap;
        }
    `,
    VerticalDiv: styled(Frame).attrs((props) => {
        return { ...props, className: `d-none d-lg-flex` };
    })`
        width: 1px;
        height: 40px;
        background: ${({ theme }) => theme.maolive};
        margin: 0 1em;
    `,
    NavItem: styled(Link)<{ $opened?: boolean }>`
        color: rgba(0, 0, 0, 0.5);
        padding: 0 1em;
        font-weight: 600;
        font-size: medium;
        display: flex;
        flex-direction: row;
        align-items: center;
        cursor: pointer;
        white-space: nowrap;

        &:hover {
            text-decoration: none;
        }

        &:after {
            content: '';
            width: 27px;
            height: 27px;
            background: url('${imgIconArrowDown}') no-repeat center center / contain;
            transform: rotate(${({ $opened }) => ($opened ? 180 : 0)}deg);
            display: ${({ as: As }: any) => (As?.target === `div` ? 'flex' : 'none')};
            transition: 0.2s;
        }

        @media (max-width: 991px) {
            /*font-family: Marcellus, serif;*/
            font-size: 15px;
            font-weight: normal;
            line-height: 19px;
            color: #343a40;
            width: 100%;
            align-items: flex-start;
            padding: 10px 25px;

            ${({ as: As }: any) =>
                As?.target === `div` &&
                css`
                    padding: 10px 20px 10px 25px;
                    width: 100%;
                    justify-content: space-between;
                `}
        }
    `,
};

const OurPlansDropdownContent = () => {
    const { t } = useResources();
    return (
        <>
            <HeaderStyles.OurPlans marketing to="/plans/simple-will">
                <h1>{t('navHeader.simple_will')}</h1>
                <p>{t('navHeader.simple_will_description')}</p>
            </HeaderStyles.OurPlans>
            <HeaderStyles.OurPlans marketing to="/plans/custom-will">
                <h1>{t('navHeader.custom_will')}</h1>
                <p>{t('navHeader.custom_will_description')}</p>
            </HeaderStyles.OurPlans>
            <HeaderStyles.OurPlans marketing to="/plans/living-trust">
                <h1>{t('navHeader.living_trust')}</h1>
                <p>{t('navHeader.living_trust_description')}</p>
            </HeaderStyles.OurPlans>
        </>
    );
};

export default Header;
